import { Static, Type } from '@sinclair/typebox';
import { PriceType, PriceTypeSchema } from './translation_namespace/price_namespace';

export const PriceInfosInputSchema = Type.Object(
  {
    price: Type.Integer({
      format: 'int32',
      minimum: 0,
    }),
    type: PriceTypeSchema({
      meta: {
        multiple: false,
        disable_search: true,
      },
    }),
  },
  {
    meta: { is_price: true, is_price_type: true },
    placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_PRICE',
    title: 'INPUT_TITLE_PRO_PRESTATION_PRICE',
  },
);
export type PriceInfosInputObject = Static<typeof PriceInfosInputSchema>;

export const PriceOptionInputSchema = Type.Object({
  // Identifiant de la formule enregistrée (renvoyé par l'éditeur) : l'enregistrement la met à jour au lieu de
  // réécrire les formules par position (un panier ou un lien qui la désigne reste valable). Absent : nouvelle.
  price_id: Type.Optional(Type.Integer({ meta: { hidden: true } })),
  // Durée optionnelle : une ligne de prix peut n'avoir aucune durée (vitrine / « sur devis »).
  // Sans durée, cette ligne n'est pas réservable en ligne (l'agenda a besoin d'une durée pour
  // bloquer un créneau) — cf. `hasConfiguredDuration` côté tunnel. C'est le miroir de
  // `PriceType.NONE` (prix optionnel) : chaque ligne porte indépendamment une durée et/ou un prix.
  duration_minutes: Type.Optional(
    Type.Integer({
      minimum: 1,
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_DURATION',
      title: 'INPUT_TITLE_PRO_PRESTATION_DURATION',
      // Masqué quand la ligne est « sur devis » (QUOTE) : non réservable, donc pas de durée à saisir.
      // Visibilité conditionnelle générique du DynamicForm — la condition cible le champ frère de
      // l'item de prix (`price_infos.type`), résolu en live (cf. evaluateFieldVisibility).
      // `is_duration_minutes` : le formulaire affiche l'équivalent en heures sous le champ (« Soit 1 h 10 min »).
      meta: { hidden_when: { field: 'price_infos.type', value: PriceType.QUOTE }, is_duration_minutes: true },
    }),
  ),
  price_infos: PriceInfosInputSchema,
  extra_buffer_minutes: Type.Optional(
    Type.Integer({
      minimum: 0,
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_BUFFER',
      title: 'INPUT_TITLE_PRO_PRESTATION_BUFFER',
      // Masqué quand la ligne est « sur devis » (QUOTE) : non réservable, donc pas de temps tampon.
      // Le backend force déjà extra_buffer_minutes à 0 pour QUOTE (cf. mapPriceOptionInputsToRows).
      meta: { hidden_when: { field: 'price_infos.type', value: PriceType.QUOTE } },
    }),
  ),
  // Libellé libre optionnel affiché dans le tunnel de réservation et sur le site public
  // pour identifier la ligne (ex : « Grande barbe », « Option premium »).
  label: Type.Optional(
    Type.String({
      maxLength: 255,
      placeholder: 'INPUT_PLACEHOLDER_PRO_PRESTATION_PRICE_LABEL',
      title: 'INPUT_TITLE_PRO_PRESTATION_PRICE_LABEL',
    }),
  ),
});
export type PriceOptionInputObject = Static<typeof PriceOptionInputSchema>;

export const PriceOptionResponseSchema = Type.Object({
  // Identifiant stable de la formule (`prestation_price_id`).
  price_id: Type.Optional(Type.Integer()),
  duration_minutes: Type.Optional(Type.Number()),
  price_infos: Type.Optional(
    Type.Object({
      price: Type.Optional(Type.Number()),
      price_string: Type.Optional(
        Type.String({
          meta: { is_price: true },
        }),
      ),
      type: Type.Optional(PriceTypeSchema()),
    }),
  ),
  extra_buffer_minutes: Type.Optional(Type.Number()),
  label: Type.Optional(Type.String()),
});
export type PriceOptionResponseObject = Static<typeof PriceOptionResponseSchema>;

export type PriceInfos = NonNullable<PriceOptionResponseObject['price_infos']>;
export type PriceOption = PriceOptionResponseObject;
export type PriceSummarySource = PriceOptionResponseObject;

/**
 * Source de vérité UNIQUE (front + back) : une ligne de prix « a une durée réservable » ssi elle
 * porte une durée strictement positive. Durée absente / nulle / 0 = ligne « vitrine / sur devis » :
 * pas de créneau réservable (l'agenda a besoin d'une durée) et rien à afficher côté durée.
 */
export const hasBookableDuration = (durationMinutes: number | null | undefined): boolean =>
  typeof durationMinutes === 'number' && durationMinutes > 0;

/**
 * Normalise un libellé de ligne de prix : trim + chaîne vide → null. Source de vérité UNIQUE du
 * stockage des libellés (côté write) pour qu'aucun blanc (`''` / `'   '`) ne finisse en base.
 */
export const normalizePriceLabel = (label: string | null | undefined): string | null => label?.trim() || null;

/**
 * Forme minimale d'une option de prix « à plat » (telle qu'exposée au public / au tunnel :
 * `PublicPriceOptionObject` côté site, `PriceOption` côté booking). Suffit pour sélectionner
 * le représentant le moins cher et décider du CTA, sans coupler aux champs de formatage.
 */
type FlatPricedOptionLike = { price?: number | null; price_type?: string; duration_minutes?: number | null };

/**
 * Source de vérité UNIQUE de la partition « réservable / sur devis » (site public + tunnel +
 * `getPrestationCtaMode`). Une ligne est réservable en ligne ssi elle porte une durée exploitable
 * ET n'est pas « sur devis » : une ligne QUOTE n'est JAMAIS réservable, même si une durée a été
 * saisie (cas legacy antérieur au forçage `duration = null` côté backend pour QUOTE).
 */
export const isBookablePriceOption = (option: FlatPricedOptionLike): boolean =>
  hasBookableDuration(option.duration_minutes) && option.price_type !== PriceType.QUOTE;

/**
 * Source de vérité UNIQUE du masquage des durées dans le tunnel : une ligne n'est proposée que si
 * elle est réservable au sens catalogue (`isBookablePriceOption`) ET que la sonde de disponibilité
 * du backend ne l'a pas écartée.
 *
 * `is_bookable` ABSENT ⇒ durée non évaluée (catalogue public, ou sonde plafonnée par
 * `MAX_DURATION_PROBES_PER_PRESTATION`) ⇒ on la garde : une durée n'est jamais masquée sans preuve.
 * Utilisé par la carte prestation, la restauration du panier et le panier externe des sites custom
 * — ne pas réécrire ce test ailleurs.
 */
export const isAvailablePriceOption = (option: FlatPricedOptionLike & { is_bookable?: boolean }): boolean =>
  isBookablePriceOption(option) && option.is_bookable !== false;

/**
 * Issue de la résolution d'une formule choisie EN AMONT du tunnel (site vitrine, lien de partage).
 * - `ok`          : ligne à mettre au panier (index dans le tableau `prices` complet).
 * - `unavailable` : la formule demandée existe toujours au catalogue mais n'est plus réservable.
 * - `none`        : aucune ligne proposable (prestation devenue 100 % vitrine, ou tout indisponible).
 */
export type PreselectedPriceResolution =
  | { status: 'ok'; price_index: number }
  | { status: 'unavailable' }
  | { status: 'none' };

/**
 * Résout la formule à mettre au panier depuis une présélection amont.
 *
 * ⚠️ Le point de tout : « la formule demandée existe-t-elle encore ? » et « est-elle réservable ? »
 * sont DEUX questions distinctes. Les fusionner en un seul test rend leurs échecs indiscernables,
 * et le repli qui suit substitue alors une autre ligne — donc un autre PRIX — en silence. Le site
 * vitrine ignorant la disponibilité (le catalogue public ne la transporte pas, à dessein), il
 * propose des formules que le tunnel masque : ce cas est nominal, pas exceptionnel.
 *
 * Le repli n'est donc légitime QUE si la formule a disparu du catalogue (le pro l'a supprimée
 * depuis la mise au panier). Et jamais sur l'index 0 : ce peut être une ligne vitrine sans durée,
 * qui effondrerait la durée totale du panier.
 */
export function resolvePreselectedPriceIndex(
  prices: ReadonlyArray<FlatPricedOptionLike & { is_bookable?: boolean }> | null | undefined,
  requestedDurationMinutes: number | null | undefined,
): PreselectedPriceResolution {
  const list = prices ?? [];

  const requestedIndex =
    requestedDurationMinutes != null
      ? list.findIndex((option): boolean => option.duration_minutes === requestedDurationMinutes)
      : -1;

  if (requestedIndex >= 0) {
    return isAvailablePriceOption(list[requestedIndex])
      ? { status: 'ok', price_index: requestedIndex }
      : { status: 'unavailable' };
  }

  const fallbackIndex = list.findIndex(isAvailablePriceOption);
  return fallbackIndex >= 0 ? { status: 'ok', price_index: fallbackIndex } : { status: 'none' };
}

/**
 * Renvoie l'option de prix non-`NONE` la **moins chère** (par `price` brut), ou `undefined` si
 * toutes les options sont `NONE`/vides. Source de vérité unique du « à partir de X€ » : le tunnel
 * (`ServiceCard`) ET les listings (`formatPrestationPriceRange`) doivent désigner le MÊME mini,
 * pas la première ligne par `sort_order`.
 */
export function getCheapestPricedOption<T extends FlatPricedOptionLike>(
  prices: ReadonlyArray<T> | null | undefined,
): T | undefined {
  const priced = (prices ?? []).filter((p): boolean => p.price_type !== PriceType.NONE);
  if (priced.length === 0) return undefined;
  return priced.reduce((min, p): T => ((p.price ?? 0) < (min.price ?? 0) ? p : min), priced[0]);
}

export type PrestationCtaMode = 'book' | 'quote' | 'contact';

/**
 * Détermine le CTA à présenter au client final pour une prestation, d'après ses lignes de prix :
 * - `book`    : au moins une ligne réservable (durée > 0) → tunnel de réservation.
 * - `quote`   : aucune ligne réservable mais au moins une ligne avec un prix exploitable
 *               (QUOTE « sur devis », ou ligne vitrine FIXED/FROM avec montant) → demande de devis.
 * - `contact` : aucune ligne réservable et aucun prix renseigné (tout `NONE`/vide) → contact.
 * Une prestation « mixte » (lignes réservables + lignes vitrine) reste `book` : la réservation prime.
 */
export function getPrestationCtaMode(
  prices: ReadonlyArray<FlatPricedOptionLike> | null | undefined,
): PrestationCtaMode {
  const list = prices ?? [];
  if (list.some(isBookablePriceOption)) return 'book';
  const hasUsablePrice = list.some((p): boolean => p.price_type !== undefined && p.price_type !== PriceType.NONE);
  return hasUsablePrice ? 'quote' : 'contact';
}

/**
 * Source de vérité UNIQUE du « au moins un prix renseigné » côté FORMULAIRE catalogue (shape
 * input imbriquée `prices[N].price_infos.type`, miroir de `getPrestationCtaMode` qui opère sur
 * la shape à plat `price_type`). Une ligne compte comme « prix setup » ssi son type est CHOISI
 * et ≠ NONE (FIXED / FREE / FROM / QUOTE — « Devis » comme « Gratuit » sont des tarifications
 * configurées) : une ligne vierge (type non sélectionné) ou « Prix indéterminé » n'en est pas un.
 * Consommée par le prédicat de visibilité `HAS_PRICED_LINE` (cf. form-visibility.ts) — `value`
 * arrive brute du form state, d'où la signature `unknown`.
 */
export const hasAnyPricedInputLine = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.some((option): boolean => {
    const type = (option as Partial<PriceOptionInputObject> | null | undefined)?.price_infos?.type;
    return type !== undefined && type !== PriceType.NONE;
  });

/**
 * Types de prix SANS montant à saisir par le pro : source de vérité unique du masquage du champ
 * « montant » dans le formulaire catalogue (`PriceInput`), qui émet alors 0 :
 * - `QUOTE` / `NONE` : aucun prix (respectivement « sur devis » et « prix indéterminé ») ;
 * - `FREE` : prix FERME à 0 € — ici le 0 est le prix réel, pas une absence de prix (le backend
 *   le refixe à 0 à l'écriture, cf. `mapPriceOptionInputsToRows`).
 */
export const isAmountlessPriceType = (priceType: string | undefined | null): boolean =>
  priceType === PriceType.NONE || priceType === PriceType.QUOTE || priceType === PriceType.FREE;

export interface PriceDisplayLabels {
  /** Préfixe « À partir de » (borne basse : type FROM ou plusieurs options). */
  price_from: string;
  /** Libellé « Sur devis » (type QUOTE : aucun prix ferme). */
  on_quote: string;
  /** Libellé « Gratuit » (type FREE : prix ferme à 0 €, jamais rendu « 0,00 € »). */
  free: string;
}

/**
 * Libellé d'UNE option de prix selon son type (source de vérité front+back) :
 * - `NONE` → '' (le pro n'a pas mis de prix → on n'affiche rien) ;
 * - `QUOTE` → « Sur devis » (pas de montant ferme) ;
 * - `FREE` → « Gratuit » (montant ferme à 0 € : on ne rend jamais « 0,00 € ») ;
 * - `FROM` → « À partir de <montant> » (borne basse) ;
 * - `FIXED` (défaut) → le montant tel quel.
 * `formattedAmount` est déjà formaté (devise/locale résolues en amont).
 */
export function formatPriceOptionLabel(
  priceType: string | undefined,
  formattedAmount: string,
  labels: PriceDisplayLabels,
): string {
  if (priceType === PriceType.NONE) return '';
  if (priceType === PriceType.QUOTE) return labels.on_quote;
  if (priceType === PriceType.FREE) return labels.free;
  if (priceType === PriceType.FROM) return `${labels.price_from} ${formattedAmount}`;
  return formattedAmount;
}

/**
 * Libellé de prix résumant TOUTES les options d'une prestation (carte de listing, en-tête tunnel).
 * Source de vérité UNIQUE partagée par le site public (`formatPrestationPriceRange`) et le tunnel
 * (`ServiceCard`) — un « sur devis » ou un « à partir de X€ » doit s'afficher pareil des deux côtés.
 * Règles :
 *  - options `NONE` exclues ; plus aucune option → `undefined` (rien à afficher) ;
 *  - aucune option FERME (tout est QUOTE) → « Sur devis » ;
 *  - une seule option ferme → on honore son type (FIXED → montant, FREE → « Gratuit »,
 *    FROM → « à partir de ») ;
 *  - plusieurs options OU représentant FROM → « À partir de <montant ferme le moins cher> »
 *    (borne basse : on garde le montant chiffré, « à partir de Gratuit » n'aurait pas de sens).
 * Le montant « à partir de » se calcule sur les options FERMES (FIXED/FROM) : une QUOTE n'a pas de
 * prix ferme et ne doit jamais tirer le minimum vers 0 €. `formatAmount` renvoie le montant déjà
 * formaté d'une option (son `price_string`, ou un formatteur local en repli côté tunnel).
 */
export function formatPriceOptionsRange<T extends FlatPricedOptionLike & { price_string?: string }>(
  prices: ReadonlyArray<T> | null | undefined,
  labels: PriceDisplayLabels,
  formatAmount: (option: T) => string,
): string | undefined {
  const priced = (prices ?? []).filter((p): boolean => p.price_type !== PriceType.NONE);
  if (priced.length === 0) return undefined;
  const firm = priced.filter((p): boolean => p.price_type !== PriceType.QUOTE);
  if (firm.length === 0) return labels.on_quote;
  const cheapest = getCheapestPricedOption(firm) ?? firm[0];
  const isLowerBound = priced.length > 1 || cheapest.price_type === PriceType.FROM;
  if (isLowerBound) return `${labels.price_from} ${formatAmount(cheapest)}`;
  return cheapest.price_type === PriceType.FREE ? labels.free : formatAmount(cheapest);
}

/**
 * Nombre d'unités facturées pour une ligne de prestation.
 *
 * Sur les lignes actuelles, `party_size` contient le nombre BRUT de personnes et `quantity` le
 * nombre de prestations achetées : les deux multiplicateurs se combinent. Sur une ligne stockée
 * legacy, `party_size = null` signifie que seule la quantité historique doit être appliquée.
 */
export const computeBillableUnits = (input: { quantity?: number | null; party_size?: number | null }): number => {
  const quantity = input.quantity && input.quantity > 0 ? input.quantity : 1;
  const partySize = input.party_size && input.party_size > 0 ? input.party_size : 1;
  return quantity * partySize;
};

/** Quantité choisie pour un supplément d'une ligne (ex. 2 peignoirs pour 3 personnes). */
export type SupplementQuantity = { supplement_id: number; quantity: number };

/**
 * Lecture tolérante d'une liste de quantités stockée (colonne JSON `supplement_quantities`) : NULL ou
 * entrée invalide → ignorée, jamais d'exception sur une ligne existante.
 */
export const readSupplementQuantities = (value: unknown): SupplementQuantity[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item): SupplementQuantity[] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const { supplement_id, quantity } = item as Record<string, unknown>;
    if (typeof supplement_id !== 'number' || typeof quantity !== 'number' || quantity < 1) return [];
    return [{ supplement_id, quantity }];
  });
};

/** Plafond proposé au client pour la quantité d'un supplément (jamais sous le nombre d'unités facturées). */
export const SUPPLEMENT_QUANTITY_MAX = 20;

export type SelectedPrestationPriceInput = {
  prestation_id: number;
  quantity: number;
  party_size?: number | null;
  selected_price_index?: number;
  selected_supplement_ids?: number[];
  /** Quantités choisies. Un supplément absent suit la règle historique : une unité par unité facturée. */
  supplement_quantities?: ReadonlyArray<SupplementQuantity> | null;
};

/**
 * Quantité facturée d'un supplément sur une ligne : celle choisie par le client ou le pro, sinon
 * une par unité facturée (prestations × personnes), la règle d'avant le choix de quantité. Une
 * ligne legacy, sans quantités, garde donc exactement son prix.
 */
export const resolveSupplementQuantity = (
  line: {
    quantity?: number | null;
    party_size?: number | null;
    supplement_quantities?: ReadonlyArray<SupplementQuantity> | null;
  },
  supplementId: number,
): number => {
  const chosen = line.supplement_quantities?.find((item): boolean => item.supplement_id === supplementId)?.quantity;
  return chosen && chosen > 0 ? chosen : computeBillableUnits(line);
};

/**
 * Part d'un supplément COUVERTE par unité d'un bon (carte cadeau, forfait) sur une ligne réservée :
 * le moins des deux entre ce que le bon prévoit par unité et ce que la ligne réserve par unité. Sans
 * quantité choisie de part et d'autre, une par unité (la règle d'avant). Fractionnaire quand les
 * quantités ne tombent pas juste (2 peignoirs pour 3 unités) : l'appelant arrondit le montant final.
 */
export const voucherSupplementCoveragePerUnit = (
  voucherItem: { units: number; supplement_quantities?: ReadonlyArray<SupplementQuantity> | null },
  bookedLine: { units: number; supplement_quantities?: ReadonlyArray<SupplementQuantity> | null },
  supplementId: number,
): number => {
  const perUnit = (line: {
    units: number;
    supplement_quantities?: ReadonlyArray<SupplementQuantity> | null;
  }): number => {
    const units = line.units > 0 ? line.units : 1;
    return (
      resolveSupplementQuantity({ quantity: units, supplement_quantities: line.supplement_quantities }, supplementId) /
      units
    );
  };
  return Math.min(perUnit(voucherItem), perUnit(bookedLine));
};

/** Quantité maximale proposable pour un supplément : le plafond, ou le nombre d'unités facturées s'il est plus grand. */
export const resolveSupplementQuantityMax = (line: { quantity?: number | null; party_size?: number | null }): number =>
  Math.max(SUPPLEMENT_QUANTITY_MAX, computeBillableUnits(line));

/**
 * Un supplément se compte au choix seulement s'il ne prend ni temps ni salle (peignoir, produit…).
 * Un supplément avec durée allongerait le rendez-vous, et un supplément lié à une salle la
 * réserverait une seconde fois : ceux-là suivent toujours la règle historique.
 */
export const isSupplementQuantitySelectable = (supplement: {
  duration_minutes?: number | null;
  pro_resource_ids?: ReadonlyArray<number> | null;
  pro_resource_id?: number | null;
}): boolean =>
  !(supplement.duration_minutes && supplement.duration_minutes > 0) &&
  !supplement.pro_resource_ids?.length &&
  supplement.pro_resource_id == null;

/**
 * Quantités à enregistrer pour une ligne : uniquement des suppléments retenus qui se comptent au
 * choix, ramenées dans [1, plafond]. Les autres suivent la règle historique et ne sont pas
 * enregistrés. Garde serveur : une saisie forgée ou périmée ne change ni le prix ni la salle réservée.
 */
export const normalizeSupplementQuantities = (
  line: {
    quantity?: number | null;
    party_size?: number | null;
    selected_supplement_ids?: ReadonlyArray<number> | null;
    supplement_quantities?: ReadonlyArray<SupplementQuantity> | null;
  },
  supplements: ReadonlyArray<{
    supplement_id: number;
    duration_minutes?: number | null;
    pro_resource_ids?: ReadonlyArray<number> | null;
    pro_resource_id?: number | null;
  }>,
): SupplementQuantity[] => {
  const selectedIds = new Set(line.selected_supplement_ids ?? []);
  const max = resolveSupplementQuantityMax(line);
  const seen = new Set<number>();
  const normalized: SupplementQuantity[] = [];
  for (const item of line.supplement_quantities ?? []) {
    if (!selectedIds.has(item.supplement_id) || seen.has(item.supplement_id)) continue;
    const supplement = supplements.find((candidate): boolean => candidate.supplement_id === item.supplement_id);
    if (!supplement || !isSupplementQuantitySelectable(supplement)) continue;
    if (!Number.isFinite(item.quantity)) continue;
    seen.add(item.supplement_id);
    normalized.push({
      supplement_id: item.supplement_id,
      quantity: Math.min(Math.max(Math.trunc(item.quantity), 1), max),
    });
  }
  return normalized;
};

export type PrestationPriceSource = {
  prestation_id: number;
  prices: Array<PriceOption> | null | undefined;
  supplements?:
    | Array<{ supplement_id: number; price?: number | null; duration_minutes?: number | null }>
    | null
    | undefined;
};

const getSelectedSupplementsTotalMinorUnit = (
  supplements: PrestationPriceSource['supplements'],
  line: SelectedPrestationPriceInput,
): number => {
  if (!line.selected_supplement_ids?.length) {
    return 0;
  }

  let totalMinorUnit = 0;

  for (const supplementId of line.selected_supplement_ids) {
    const supplement = supplements?.find((item): boolean => item.supplement_id === supplementId);
    totalMinorUnit += (supplement?.price ?? 0) * resolveSupplementQuantity(line, supplementId);
  }

  return totalMinorUnit;
};

const getSelectedSupplementsTotalDurationMinutes = (
  supplements: PrestationPriceSource['supplements'],
  selectedSupplementIds: number[] | undefined,
): number => {
  if (!selectedSupplementIds?.length) {
    return 0;
  }

  let totalDurationMinutes = 0;

  for (const supplementId of selectedSupplementIds) {
    const supplement = supplements?.find((item): boolean => item.supplement_id === supplementId);
    totalDurationMinutes += supplement?.duration_minutes ?? 0;
  }

  return totalDurationMinutes;
};

/**
 * Montant FERME (unité mineure) de l'option sélectionnée, ou `null` si le prix n'est pas ferme
 * (FROM / QUOTE / NONE / type absent) — auquel cas le total de la réservation est indéterminé.
 * `FREE` est un prix ferme à 0 € (équivalent d'un FIXED à 0) : il renvoie 0, jamais `null`,
 * sinon un panier 100 % gratuit basculerait à tort en « total à déterminer ».
 */
export const getSelectedFixedPriceMinorUnit = (
  prices: Array<PriceOption> | null | undefined,
  selectedPriceIndex?: number,
): number | null => {
  const priceIndex = selectedPriceIndex ?? 0;
  const selectedPriceInfos = prices?.[priceIndex]?.price_infos ?? prices?.[0]?.price_infos;

  if (selectedPriceInfos?.type === PriceType.FREE) {
    return 0;
  }

  if (
    selectedPriceInfos?.type !== PriceType.FIXED ||
    selectedPriceInfos.price === undefined ||
    selectedPriceInfos.price === null
  ) {
    return null;
  }

  return selectedPriceInfos.price;
};

export const getSelectedDurationMinutes = (
  prices: Array<PriceOption> | null | undefined,
  selectedPriceIndex?: number,
): number | null => {
  const priceIndex = selectedPriceIndex ?? 0;
  const selectedDuration = prices?.[priceIndex]?.duration_minutes ?? prices?.[0]?.duration_minutes;

  if (selectedDuration === undefined || selectedDuration === null) {
    return null;
  }

  return selectedDuration;
};

/**
 * Durée de service d'une ligne : durée tarifaire et suppléments sélectionnés, répétés
 * `quantity` fois. Le tampon de l'option de prix n'en fait pas partie : il est appliqué au blocage
 * professionnel par `buildChainSegments`.
 */
export const computePrestationLineDurationMinutes = (
  prestationInput: SelectedPrestationPriceInput,
  prestation: PrestationPriceSource,
): number | null => {
  const selectedDurationMinutes = getSelectedDurationMinutes(prestation.prices, prestationInput.selected_price_index);

  if (selectedDurationMinutes === null) {
    return null;
  }

  const supplementsDurationMinutes = getSelectedSupplementsTotalDurationMinutes(
    prestation.supplements,
    prestationInput.selected_supplement_ids,
  );

  return (selectedDurationMinutes + supplementsDurationMinutes) * prestationInput.quantity;
};

export const computeFixedPrestationsTotalPrice = (
  prestationsInput: Array<SelectedPrestationPriceInput>,
  prestations: Array<PrestationPriceSource>,
): number | null => {
  if (!prestationsInput.length) return null;

  let totalMinorUnit = 0;
  for (const prestationInput of prestationsInput) {
    const prestation = prestations.find(
      (prestationItem): boolean => prestationInput.prestation_id === prestationItem.prestation_id,
    );
    if (!prestation) continue;

    const selectedMinorUnitPrice = getSelectedFixedPriceMinorUnit(
      prestation.prices,
      prestationInput.selected_price_index,
    );
    if (selectedMinorUnitPrice === null) {
      return null;
    }

    // Prestation × unités facturées, puis chaque supplément × sa quantité (par défaut ces mêmes unités).
    totalMinorUnit +=
      selectedMinorUnitPrice * computeBillableUnits(prestationInput) +
      getSelectedSupplementsTotalMinorUnit(prestation.supplements, prestationInput);
  }

  return totalMinorUnit;
};

export const computePrestationsTotalDuration = (
  prestationsInput: Array<SelectedPrestationPriceInput>,
  prestations: Array<PrestationPriceSource>,
): number | null => {
  if (!prestationsInput.length) return null;

  let totalDurationMinutes = 0;
  let hasDuration = false;

  for (const prestationInput of prestationsInput) {
    const prestation = prestations.find(
      (prestationItem): boolean => prestationInput.prestation_id === prestationItem.prestation_id,
    );
    if (!prestation) continue;

    const selectedDurationMinutes = getSelectedDurationMinutes(prestation.prices, prestationInput.selected_price_index);
    if (selectedDurationMinutes === null) {
      return null;
    }

    totalDurationMinutes += selectedDurationMinutes * prestationInput.quantity;
    hasDuration = true;
  }

  return hasDuration ? totalDurationMinutes : null;
};
